import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Card,
  CardBody,
  Badge,
  Avatar,
  Button,
  Spinner,
  Alert,
  AlertIcon,
  useColorModeValue,
  useToast,
  Grid,
  GridItem,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Tooltip,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useThemeContext } from '@/contexts/ThemeContext';
import { useTeamService } from '@/hooks/useTeamService';

// Icons
const MoreIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
  </svg>
);

const UserIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
  </svg>
);

interface TeamMember {
  user: {
    identity: {
      user_id: string;
      profile_id: string;
    };
    username: string;
    display_name?: string;
    first_name?: string;
    last_name?: string;
    email?: string;
    avatar_url?: string;
    status: string;
    joined_at: string;
  };
  team_id: string;
  role: string;
  roles?: Array<{
    id: string;
    name: string;
    description?: string;
  }>;
  status: string;
  joined_at: string;
}

interface TeamMembersTabProps {
  teamId: string;
  permissions: {
    can_view_team: boolean;
    can_add_members: boolean;
    can_remove_members: boolean;
    can_invite_users: boolean;
    can_manage_roles: boolean;
  };
  isActive: boolean;
  onRemoveMember?: (userId: string) => void;
}

export const TeamMembersTab: React.FC<TeamMembersTabProps> = ({
  teamId,
  permissions,
  isActive,
  onRemoveMember,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { colorSchemes } = useThemeContext();
  const teamService = useTeamService();

  // Color mode values
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.600', 'gray.400');
  const cardBg = useColorModeValue('white', 'gray.800');

  // State
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [hasLoaded, setHasLoaded] = useState(false);

  // Load members data only when tab becomes active
  const loadMembers = async () => {
    if (!teamId || !permissions.can_view_team) return;

    try {
      setIsLoading(true);
      setError('');
      
      const membersData = await teamService.getTeamMembers(teamId);
      setMembers(membersData || []);
      setHasLoaded(true);
    } catch (err: any) {
      console.error('Error loading team members:', err);
      setError(err.message || 'Failed to load team members');
      toast({
        title: t('common.error'),
        description: err.message || 'Failed to load team members',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Load data when tab becomes active
  useEffect(() => {
    if (isActive && !hasLoaded && permissions.can_view_team) {
      loadMembers();
    }
  }, [isActive, hasLoaded, permissions.can_view_team, teamId]);

  // Get avatar source with fallback
  const getAvatarSrc = (member: TeamMember) => {
    if (member.user?.avatar_url) {
      return member.user.avatar_url.startsWith('http') 
        ? member.user.avatar_url 
        : `/api/v1/users/${member.user.identity.user_id}/avatar`;
    }
    return undefined;
  };

  // Get display name with fallback
  const getDisplayName = (member: TeamMember) => {
    if (member.user?.display_name) return member.user.display_name;
    if (member.user?.first_name && member.user?.last_name) {
      return `${member.user.first_name} ${member.user.last_name}`;
    }
    return member.user?.username || 'Unknown User';
  };

  // Get role badge color
  const getRoleBadgeColor = (role: string) => {
    switch (role?.toLowerCase()) {
      case 'owner':
        return 'purple';
      case 'admin':
        return 'blue';
      case 'moderator':
        return 'green';
      case 'member':
        return 'gray';
      default:
        return 'gray';
    }
  };

  // Handle member click
  const handleMemberClick = (member: TeamMember) => {
    navigate(`/users/${member.user.identity.user_id}`);
  };

  // Handle remove member
  const handleRemoveMember = async (member: TeamMember) => {
    if (!permissions.can_remove_members) return;
    
    if (window.confirm(`Are you sure you want to remove ${getDisplayName(member)} from the team?`)) {
      try {
        if (onRemoveMember) {
          await onRemoveMember(member.user.identity.user_id);
          // Reload members after removal
          await loadMembers();
        }
      } catch (error) {
        console.error('Error removing member:', error);
      }
    }
  };

  // Permission check
  if (!permissions.can_view_team) {
    return (
      <Box textAlign="center" py={8}>
        <Alert status="warning">
          <AlertIcon />
          {t('teams.noPermissionToViewMembers')}
        </Alert>
      </Box>
    );
  }

  // Loading state
  if (isLoading && !hasLoaded) {
    return (
      <VStack spacing={4} py={8}>
        <Spinner size="lg" />
        <Text>{t('teams.loadingMembers')}</Text>
      </VStack>
    );
  }

  // Error state
  if (error && !isLoading) {
    return (
      <Box textAlign="center" py={8}>
        <Alert status="error">
          <AlertIcon />
          {error}
        </Alert>
        <Button mt={4} onClick={loadMembers} colorScheme={colorSchemes.primary}>
          {t('common.retry')}
        </Button>
      </Box>
    );
  }

  // Empty state
  if (!isLoading && members.length === 0) {
    return (
      <Box textAlign="center" py={8}>
        <Text color={textColor} fontSize="lg">
          {t('teams.noMembers')}
        </Text>
        {permissions.can_invite_users && (
          <Text color={textColor} fontSize="sm" mt={2}>
            {t('teams.inviteFirstMember')}
          </Text>
        )}
      </Box>
    );
  }

  return (
    <VStack spacing={4} align="stretch">
      {/* Header */}
      <HStack justify="space-between" align="center">
        <Text fontSize="lg" fontWeight="semibold">
          {t('teams.teamMembers')} ({members.length})
        </Text>
        <Button
          size="sm"
          onClick={loadMembers}
          isLoading={isLoading}
          colorScheme={colorSchemes.neutral}
          variant="outline"
        >
          {t('common.refresh')}
        </Button>
      </HStack>

      {/* Members Grid */}
      <Grid templateColumns="repeat(auto-fill, minmax(300px, 1fr))" gap={4}>
        {members.map((member) => (
          <GridItem key={`${member.user.identity.user_id}-${member.team_id}`}>
            <Card
              bg={cardBg}
              borderWidth={1}
              borderColor={borderColor}
              cursor="pointer"
              transition="all 0.2s"
              _hover={{
                transform: 'translateY(-2px)',
                shadow: 'md',
                borderColor: `${colorSchemes.primary}.300`,
              }}
              onClick={() => handleMemberClick(member)}
            >
              <CardBody>
                <HStack spacing={3} align="start">
                  {/* Avatar */}
                  <Avatar
                    size="md"
                    name={getDisplayName(member)}
                    src={getAvatarSrc(member)}
                    bg={`${colorSchemes.primary}.500`}
                    color="white"
                  />

                  {/* Member Info */}
                  <VStack align="start" spacing={1} flex={1} minW={0}>
                    <HStack justify="space-between" w="full">
                      <VStack align="start" spacing={0} flex={1} minW={0}>
                        <Text fontWeight="semibold" fontSize="md" noOfLines={1}>
                          {getDisplayName(member)}
                        </Text>
                        {member.user?.username && (
                          <Text fontSize="sm" color={textColor} noOfLines={1}>
                            @{member.user.username}
                          </Text>
                        )}
                        {member.user?.email && (
                          <Text fontSize="xs" color={textColor} noOfLines={1}>
                            {member.user.email}
                          </Text>
                        )}
                      </VStack>

                      {/* Actions Menu */}
                      {permissions.can_remove_members && (
                        <Menu>
                          <MenuButton
                            as={IconButton}
                            icon={<MoreIcon />}
                            size="sm"
                            variant="ghost"
                            onClick={(e) => e.stopPropagation()}
                          />
                          <MenuList>
                            <MenuItem
                              icon={<UserIcon />}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMemberClick(member);
                              }}
                            >
                              {t('common.viewProfile')}
                            </MenuItem>
                            {permissions.can_remove_members && (
                              <MenuItem
                                color="red.500"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoveMember(member);
                                }}
                              >
                                {t('teams.removeMember')}
                              </MenuItem>
                            )}
                          </MenuList>
                        </Menu>
                      )}
                    </HStack>

                    {/* Role and Status */}
                    <HStack spacing={2} mt={2}>
                      <Badge
                        colorScheme={getRoleBadgeColor(member.role)}
                        variant="subtle"
                        size="sm"
                      >
                        {member.role || 'Member'}
                      </Badge>
                      <Badge
                        colorScheme={member.status === 'active' ? 'green' : 'gray'}
                        variant="outline"
                        size="sm"
                      >
                        {member.status}
                      </Badge>
                    </HStack>

                    {/* Join Date */}
                    <Text fontSize="xs" color={textColor}>
                      {t('teams.joinedOn')}: {new Date(member.joined_at).toLocaleDateString()}
                    </Text>

                    {/* Additional Roles */}
                    {member.roles && member.roles.length > 1 && (
                      <HStack spacing={1} mt={1}>
                        <Text fontSize="xs" color={textColor}>
                          {t('teams.additionalRoles')}:
                        </Text>
                        {member.roles.slice(1).map((role) => (
                          <Tooltip key={role.id} label={role.description} hasArrow>
                            <Badge size="xs" variant="outline">
                              {role.name}
                            </Badge>
                          </Tooltip>
                        ))}
                      </HStack>
                    )}
                  </VStack>
                </HStack>
              </CardBody>
            </Card>
          </GridItem>
        ))}
      </Grid>
    </VStack>
  );
}; 